/**
 * conversations.ts — HTTP layer for the assistant conversations REST API.
 *
 * All requests go through apiFetch (lib/http), which injects auth headers,
 * workspace context, and a 10s timeout. The server-side model is:
 *   GET    /api/conversations              — list caller's own conversations
 *   GET    /api/conversations/:id/messages — fetch the UIMessage[] blob
 *   PATCH  /api/conversations/:id          — update title / isPinned / isArchived
 *   DELETE /api/conversations/:id          — delete a conversation
 */

import { apiFetch } from '@/lib/http';
import type { UIMessage } from 'ai';

export interface ConversationListItem {
  id: string;
  title: string;
  isPinned: boolean;
  isArchived: boolean;
  latestActivityAt: string;
}

/**
 * Fetch the list of conversations visible to the current user in the active
 * workspace. Pinned first, then most-recent activity.
 */
export async function fetchConversations(): Promise<ConversationListItem[]> {
  return apiFetch<ConversationListItem[]>('/conversations');
}

/**
 * Fetch the persisted UIMessage[] blob for a conversation.
 * Returns the array directly — the blob is already in Vercel SDK v6 UIMessage
 * format (cortex emits it via chat_messages_to_ui_messages) and can be
 * hydrated directly into a Chat instance via the messages setter.
 *
 * Returns an empty array when no messages have been persisted yet.
 */
export async function fetchConversationMessages(id: string): Promise<UIMessage[]> {
  const res = await apiFetch<{ messages: UIMessage[] }>(`/conversations/${id}/messages`);
  return res.messages ?? [];
}

/**
 * PATCH /api/conversations/:id — update title, pin state, or archive state.
 * The server persists the change and emits a sync event so the entity store
 * receives the updated ConversationRow via the normal sync path.
 */
export async function updateConversation(
  id: string,
  patch: { title?: string; isPinned?: boolean; isArchived?: boolean },
): Promise<void> {
  await apiFetch(`/conversations/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

/**
 * DELETE /api/conversations/:id — permanently delete a conversation.
 * The server emits a delete sync event so the entity store removes the entry.
 */
export async function deleteConversation(id: string): Promise<void> {
  await apiFetch(`/conversations/${id}`, { method: 'DELETE' });
}
